import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ChevronDown, Minus, Plus, X, Images, ArrowUpRight, FileSignature } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl, formatApiErrorDetail, fmtDateUS } from "../lib/api";

const fmt = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const KineticLines = ({ lines, className }) => (
  <h1 className={className}>
    {lines.map((l) => (
      <span key={l} className="block overflow-hidden pb-1 -mb-1">
        <span className="block">{l}</span>
      </span>
    ))}
  </h1>
);

/* ---------- legacy components (proposals created before type-choice model) ---------- */

const OptionRow = ({ item, selected, detailOpen, onToggleDetail, onAdd }) => (
  <div className={`border transition-all duration-300 overflow-hidden ${selected ? "border-primary bg-primary/[0.07]" : "border-white/15 hover:border-white/30"}`} data-testid={`option-${item.id}`}>
    <div className="flex">
      {(item.subcategory_photo || item.photos?.length > 0) && (
        <img src={mediaUrl(item.subcategory_photo || item.photos[0])} alt={item.name} className="w-24 sm:w-36 object-cover shrink-0" loading="lazy" />
      )}
      <div className="flex-1 p-4 sm:p-5 min-w-0">
        <p className={`font-display text-xl sm:text-2xl tracking-wide transition-colors duration-300 ${selected ? "text-primary" : ""}`}>{item.name}{item.subcategory ? <span className="text-primary"> — {item.subcategory}</span> : null}</p>
        {item.customer_description && <p className="text-white/50 text-xs sm:text-sm mt-1 leading-relaxed">{item.customer_description}</p>}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3">
          <span className="text-sm font-bold text-primary">{fmt(item.total)}</span>
          <span className="text-[11px] text-white/40">{item.quantity} {item.unit} · price & labor included</span>
          <button onClick={onToggleDetail} className="text-[11px] font-bold uppercase tracking-widest text-white/50 hover:text-primary flex items-center gap-1 transition-colors duration-200" data-testid={`detail-toggle-${item.id}`}>
            Details & photos <ChevronDown size={12} className={`transition-transform duration-300 ${detailOpen ? "rotate-180" : ""}`} aria-hidden="true" />
          </button>
        </div>
        {detailOpen && (
          <div className="mt-3 space-y-2.5 border-t border-white/10 pt-3" data-testid={`detail-panel-${item.id}`}>
            {item.special_details && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-primary mb-1">Special Details / Specifications</p>
                <p className="text-white/60 text-xs leading-relaxed whitespace-pre-line">{item.special_details}</p>
              </div>
            )}
            {(item.subcategory_photo || item.photos?.length > 0) && (
              <div className="space-y-2">
                <img src={mediaUrl(item.subcategory_photo || item.photos[0])} alt={item.name} className="w-full aspect-video object-cover" />
                {item.photos?.length > 1 && (
                  <div className="grid grid-cols-3 gap-2">
                    {item.photos.slice(1).map((p) => <img key={p} src={mediaUrl(p)} alt="" className="w-full aspect-square object-cover" loading="lazy" />)}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col justify-center pr-4 sm:pr-5">
        {selected ? (
          <button onClick={onAdd} aria-label="Remove from project" data-testid={`remove-from-project-${item.id}`} className="w-10 h-10 sm:w-11 sm:h-11 border border-primary text-primary flex items-center justify-center transition-colors duration-300 hover:bg-primary/10">
            <Minus size={17} aria-hidden="true" />
          </button>
        ) : (
          <button onClick={onAdd} aria-label="Add to project" data-testid={`add-to-project-${item.id}`} className="w-10 h-10 sm:w-11 sm:h-11 bg-primary text-primary-foreground flex items-center justify-center transition-transform duration-300 hover:scale-105">
            <Plus size={17} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  </div>
);

const ChoiceStep = ({ num, group, gItems, selectedId, open, onToggle, onSelect, detailId, onToggleDetail }) => {
  const chosen = gItems.find((i) => i.id === selectedId);
  return (
    <div className={`border transition-colors duration-300 ${selectedId ? "border-primary/50" : "border-white/15"}`} data-testid={`step-${group}`}>
      <button onClick={onToggle} className="w-full p-4 sm:p-5 flex items-center gap-4 text-left" data-testid={`step-toggle-${group}`}>
        <span className={`font-display text-3xl sm:text-4xl leading-none transition-colors duration-300 ${selectedId ? "text-primary" : "text-white/25"}`}>{String(num).padStart(2, "0")}</span>
        <span className="flex-1 min-w-0">
          <span className="block text-[10px] font-bold uppercase tracking-[0.25em] text-white/45">{gItems[0].category}</span>
          <span className="block font-display text-xl sm:text-2xl tracking-wide mt-0.5">{group}</span>
          {chosen ? (
            <span className="text-sm font-semibold text-primary flex items-center gap-2"><Check size={14} aria-hidden="true" /> {chosen.subcategory_photo && <img src={mediaUrl(chosen.subcategory_photo)} alt="" className="w-8 h-8 object-cover border border-primary/30" data-testid={`chosen-photo-${group}`} />} {chosen.name}{chosen.subcategory ? ` — ${chosen.subcategory}` : ""}</span>
          ) : (
            <span className="text-xs text-white/40 italic">Tap to choose one of {gItems.length} options</span>
          )}
        </span>
        <ChevronDown size={20} className={`shrink-0 text-white/50 transition-transform duration-300 ${open ? "rotate-180 text-primary" : ""}`} aria-hidden="true" />
      </button>
      <div className={`grid transition-[grid-template-rows] duration-500 ease-out ${open ? "[grid-template-rows:1fr]" : "[grid-template-rows:0fr]"}`}>
        <div className="overflow-hidden">
          <div className="p-4 sm:p-5 pt-1 space-y-3 border-t border-white/10">
            {gItems.map((item) => (
              <OptionRow
                key={item.id}
                item={item}
                selected={selectedId === item.id}
                detailOpen={detailId === item.id}
                onToggleDetail={() => onToggleDetail(item.id)}
                onAdd={() => onSelect(item.id)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const ItemCard = ({ item, selectable, selected, onToggle, onZoom }) => {
  const [showExamples, setShowExamples] = useState(false);
  const photos = [...new Set([item.subcategory_photo, ...(item.photos || [])].filter(Boolean))];
  return (
  <div className={`rounded-2xl border transition-all duration-300 overflow-hidden shadow-[0_8px_28px_rgba(0,0,0,0.4)] ${selected && selectable ? "border-primary/70 bg-secondary" : "border-white/20 bg-card"} ${!selectable ? "opacity-90" : ""}`} data-testid={`item-${item.id}`}>
    <div className="p-4 sm:p-5 flex items-start justify-between gap-4">
      <div className="flex-1 min-w-0">
        <h3 className="font-bold text-base">{item.name}{item.subcategory ? <span className="text-primary"> — {item.subcategory}</span> : null}</h3>
        {item.customer_description && <p className="text-white/50 text-xs sm:text-sm mt-1.5 leading-relaxed">{item.customer_description}</p>}
        {item.special_details && (
          <div className="mt-2.5 border-l-2 border-primary/50 pl-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-primary mb-0.5">Special Details</p>
            <p className="text-white/60 text-xs leading-relaxed whitespace-pre-line">{item.special_details}</p>
          </div>
        )}
        {photos.length > 0 && (
          <div className="mt-2.5">
            <button onClick={(e) => { e.stopPropagation(); setShowExamples(!showExamples); }} data-testid={`examples-toggle-${item.id}`} className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-white/50 hover:text-primary transition-colors duration-200">
              <Images size={13} aria-hidden="true" /> {showExamples ? "Hide examples" : `See examples of this material (${photos.length})`}
            </button>
            {showExamples && (
              <div className="grid grid-cols-3 gap-2 mt-2">
                {photos.map((p) => (
                  <button key={p} onClick={(e) => { e.stopPropagation(); onZoom(p); }} data-testid={`example-photo-${p.split("/").pop()}`} className="aspect-square overflow-hidden border border-white/10 hover:border-primary transition-colors duration-200" aria-label="Enlarge example photo">
                    <img src={mediaUrl(p)} alt="Material example" className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="text-right shrink-0">
        {selectable && (
          <div className={`w-6 h-6 border-2 flex items-center justify-center mb-2 ml-auto transition-colors duration-300 ${selected ? "bg-primary border-primary" : "border-white/30"}`}>
            {selected && <Check size={15} className="text-primary-foreground" aria-hidden="true" />}
          </div>
        )}
        {item.flag === "included" ? (
          <span className="text-white/50 text-sm font-semibold">Included</span>
        ) : (
          <>
            <p className={`font-display text-2xl leading-none ${selected || item.flag === "required" ? "text-primary" : "text-white/50"}`}>{fmt(item.total)}</p>
            <p className="text-[11px] text-white/40 mt-1">{item.quantity} {item.unit} × ${item.rate}</p>
          </>
        )}
      </div>
    </div>
  </div>
  );
};

/* ---------- new type-choice model ---------- */

const TypeCard = ({ item, pick, open, onToggleOpen, onPickOption, onZoom }) => {
  const [showExamples, setShowExamples] = useState(false);
  const options = item.offered_options || [];
  const chosenOpt = pick ? options.find((o) => o.id === pick.sub_id) : null;
  const minRate = options.length ? Math.min(...options.map((o) => o.rate)) : 0;
  const examplePhotos = [...new Set([...(item.photos || []), ...options.map((o) => o.photo).filter(Boolean)])];
  return (
    <div className={`rounded-2xl border transition-all duration-300 overflow-hidden shadow-[0_8px_28px_rgba(0,0,0,0.4)] ${pick ? "border-primary/70 bg-secondary" : "border-white/20 bg-card hover:border-white/35"}`} data-testid={`type-card-${item.id}`}>
      <button onClick={onToggleOpen} className="w-full p-4 sm:p-5 flex items-center gap-4 text-left" data-testid={`type-toggle-${item.id}`}>
        <div className="flex-1 min-w-0">
          <p className={`font-display text-xl sm:text-2xl tracking-wide transition-colors duration-300 ${pick ? "text-primary" : ""}`}>{item.name}</p>
          <p className="text-[11px] text-white/45 mt-1">{item.quantity} {item.unit} · {options.length} choice{options.length === 1 ? "" : "s"} inside</p>
          {pick && chosenOpt && (
            <p className="text-primary text-xs font-semibold mt-1.5 flex items-center gap-1.5"><Check size={12} aria-hidden="true" /> {chosenOpt.name} — in your project</p>
          )}
        </div>
        <div className="text-right shrink-0">
          {pick && chosenOpt ? (
            <>
              <p className="font-display text-2xl leading-none text-primary">{fmt(item.quantity * chosenOpt.rate)}</p>
              <p className="text-[11px] text-white/40 mt-1">{item.quantity} {item.unit} × ${chosenOpt.rate}</p>
            </>
          ) : (
            <>
              <p className="font-display text-lg leading-none text-white/50">from {fmt(item.quantity * minRate)}</p>
              <p className="text-[10px] text-white/35 mt-1">tap to see choices</p>
            </>
          )}
          <ChevronDown size={18} className={`ml-auto mt-2 text-white/50 transition-transform duration-300 ${open ? "rotate-180 text-primary" : ""}`} aria-hidden="true" />
        </div>
      </button>
      {open && (
        <div className={`border-t px-4 sm:px-5 py-4 space-y-2.5 bg-background/60 ${pick ? "border-primary/25" : "border-white/10"}`} data-testid={`type-options-${item.id}`}>
          <p className="text-[10px] font-bold uppercase tracking-widest text-white/40">{pick ? "Your pick — tap it again to remove it" : "Tap one to add it to your project"}</p>
          {options.map((o) => {
            const active = pick?.sub_id === o.id;
            return (
              <button key={o.id} onClick={() => onPickOption(item.id, o.id)} data-testid={`type-option-${item.id}-${o.id}`} className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors duration-200 ${active ? "border-primary bg-primary/10" : "border-white/15 bg-white/[0.03] hover:border-white/35"}`}>
                {o.photo && <img src={mediaUrl(o.photo)} alt={o.name} className="w-12 h-12 object-cover shrink-0 border border-white/10" loading="lazy" />}
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-white/90">{o.name}</span>
                  {o.description && <span className="block text-[11px] text-white/45 leading-snug">{o.description}</span>}
                </span>
                <span className="text-right shrink-0">
                  <span className={`block font-bold text-sm ${active ? "text-primary" : "text-white/70"}`}>{fmt(item.quantity * o.rate)}</span>
                  <span className="block text-[10px] text-white/40">${o.rate}/{item.unit}</span>
                </span>
                <span className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors duration-200 ${active ? "border-primary" : "border-white/30"}`}>
                  {active && <span className="w-2 h-2 rounded-full bg-primary" />}
                </span>
              </button>
            );
          })}
          {examplePhotos.length > 0 && (
            <div className="pt-1">
              <button onClick={() => setShowExamples(!showExamples)} data-testid={`examples-toggle-${item.id}`} className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-white/50 hover:text-primary transition-colors duration-200">
                <Images size={13} aria-hidden="true" /> {showExamples ? "Hide examples" : `See examples of this material (${examplePhotos.length})`}
              </button>
              {showExamples && (
                <div className="grid grid-cols-3 gap-2 mt-2.5" data-testid={`examples-grid-${item.id}`}>
                  {examplePhotos.map((p) => (
                    <button key={p} onClick={() => onZoom(p)} data-testid={`example-photo-${p.split("/").pop()}`} className="aspect-square overflow-hidden border border-white/10 hover:border-primary transition-colors duration-200" aria-label="Enlarge example photo">
                      <img src={mediaUrl(p)} alt="Material example" className="w-full h-full object-cover" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
              {showExamples && <p className="text-[10px] text-white/35 mt-1.5">Tap any photo to see it bigger. Example photos — your house may look different.</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function ProposalView() {
  const { id } = useParams();
  const [proposal, setProposal] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [optionalSel, setOptionalSel] = useState(new Set());
  const [altSel, setAltSel] = useState({});
  const [sel, setSel] = useState({});
  const [openTypes, setOpenTypes] = useState(new Set());
  const [lightbox, setLightbox] = useState(null);
  const [openGroup, setOpenGroup] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editNote, setEditNote] = useState("");
  const [editSent, setEditSent] = useState(false);
  const [editSending, setEditSending] = useState(false);
  const [clientNote, setClientNote] = useState("");
  const [signatureName, setSignatureName] = useState("");

  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
    api.get(`/proposals/${id}`)
      .then(({ data }) => {
        setProposal(data);
        if (data.status === "accepted" && data.client_selection) setDone(data.client_selection);
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 px-5 text-center" data-testid="proposal-not-found">
        <img src="/logo.png" alt="Infinite Siding LLC" className="h-12 w-auto logo-on-dark" />
        <h1 className="font-display text-4xl mt-6">PROPOSAL NOT FOUND</h1>
        <p className="text-white/50 text-sm">This link may be expired or incorrect. Call us at (203) 307-5222.</p>
      </div>
    );
  }
  if (!proposal) return <div className="min-h-screen bg-background flex items-center justify-center text-white/50 text-sm">Loading proposal...</div>;

  const toggleOptional = (itemId) => {
    setOptionalSel((prev) => {
      const next = new Set(prev);
      next.has(itemId) ? next.delete(itemId) : next.add(itemId);
      return next;
    });
  };

  const items = proposal.items;
  const categories = [...new Set(items.map((i) => i.category))];
  const modes = proposal.category_modes || {};
  const newModel = items.some((i) => i.offered_options?.length) || Object.keys(modes).length > 0;

  /* legacy model helpers */
  const altGroups = {};
  items.filter((i) => i.flag === "alternate").forEach((i) => {
    (altGroups[i.option_group] = altGroups[i.option_group] || []).push(i);
  });
  const allGroups = Object.entries(altGroups);
  const includedItems = items.filter((i) => i.flag === "required" || i.flag === "included");

  /* new model helpers */
  const toggleOpen = (itemId) => {
    setOpenTypes((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
      return next;
    });
  };
  const pickOption = (cat, itemId, subId) => {
    setSel((prev) => {
      const picks = prev[cat] || [];
      const existing = picks.find((p) => p.item_id === itemId);
      if (existing && existing.sub_id === subId) {
        // Tapping the chosen option again removes it from the project
        return { ...prev, [cat]: picks.filter((p) => p.item_id !== itemId) };
      }
      const mode = modes[cat] || "single";
      const base = mode === "single" ? [] : picks.filter((p) => p.item_id !== itemId);
      return { ...prev, [cat]: [...base, { item_id: itemId, sub_id: subId }] };
    });
  };

  const total = newModel
    ? items.reduce((s, i) => (i.flag === "required" ? s + i.total : s), 0) +
      Object.values(sel).flat().reduce((s, p) => {
        const item = items.find((i) => i.id === p.item_id);
        const opt = item?.offered_options?.find((o) => o.id === p.sub_id);
        return s + (item && opt ? item.quantity * opt.rate : 0);
      }, 0)
    : items.reduce((sum, i) => {
        if (i.flag === "required") return sum + i.total;
        if (i.flag === "optional" && optionalSel.has(i.id)) return sum + i.total;
        if (i.flag === "alternate" && altSel[i.option_group] === i.id) return sum + i.total;
        return sum;
      }, 0);

  const submit = async () => {
    if (!newModel) {
      const unchosen = allGroups.filter(([g]) => !altSel[g]);
      if (unchosen.length > 0) {
        toast.error(`Almost there — choose your ${unchosen[0][0].toLowerCase()} first`);
        setOpenGroup(unchosen[0][0]);
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = newModel
        ? {
            type_choices: Object.fromEntries(
              Object.entries(sel)
                .filter(([, picks]) => picks.length > 0)
                .map(([cat, picks]) => [cat, picks.map((p) => ({ item_id: p.item_id, sub_id: p.sub_id }))])
            ),
            client_note: clientNote,
            approve_only: true,
          }
        : { selected_optional: [...optionalSel], alternates: altSel, client_note: clientNote, approve_only: true };
      const { data } = await api.post(`/proposals/${id}/submit`, payload);
      if (data.needs_signature) {
        // prices approved — move on to the contract signing step
        const fresh = await api.get(`/proposals/${id}`);
        setProposal(fresh.data);
      } else {
        setDone({ total: data.total, signed: "" });
      }
      window.__lenis?.scrollTo(0, { immediate: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const signContract = async () => {
    if (!signatureName.trim()) {
      toast.error("Type your full name to sign");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/proposals/${id}/sign`, { signature_name: signatureName.trim() });
      setDone({ total: proposal.client_selection?.total, signed: signatureName.trim(), signed_at: data.signed_at });
      window.__lenis?.scrollTo(0, { immediate: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const sendEditRequest = async () => {
    setEditSending(true);
    try {
      await api.post(`/proposals/${id}/request-edit`, { note: editNote });
      setEditSent(true);
      setEditOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setEditSending(false);
    }
  };

  if (proposal.status === "approved" && !done) {
    /* STEP 2 — prices approved, now review the contract and sign (or sign later / request a change) */
    const seln = proposal.client_selection || {};
    const approvedLines = seln.chosen_lines != null
      ? [...proposal.items.filter((i) => i.flag === "required"), ...(seln.chosen_lines || [])]
      : proposal.items.filter((i) =>
          i.flag === "required"
          || (i.flag === "optional" && (seln.selected_optional || []).includes(i.id))
          || (i.flag === "alternate" && (seln.alternates || {})[i.option_group] === i.id));
    const approvedTotal = seln.total ?? approvedLines.reduce((s, i) => s + (i.total || 0), 0);
    const approvedDate = fmtDateUS(seln.approved_at) || new Date().toLocaleDateString("en-US");
    const todayLong = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    return (
      <div className="min-h-screen bg-background pb-16" data-testid="signing-step">
        <header className="border-b border-white/10 px-5 md:px-10 py-5 flex items-center justify-center">
          <img src="/logo.png" alt="Infinite Siding LLC" className="h-11 w-auto logo-on-dark" />
        </header>
        <main className="max-w-2xl mx-auto px-5 pt-10 space-y-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <p className="text-primary text-xs font-bold tracking-[0.3em] uppercase mb-3">Prices approved — one last step</p>
            <h1 className="font-display text-5xl md:text-6xl leading-[0.95] tracking-wide">REVIEW &<br />SIGN</h1>
          </motion.div>

          <section className="border border-white/10 rounded-2xl bg-card p-5 space-y-2" data-testid="signing-info">
            <p className="text-[10px] font-bold uppercase tracking-widest text-primary">Project</p>
            <p className="font-bold text-white/90">{proposal.title}</p>
            <p className="text-sm text-white/60">{proposal.client_name}{proposal.client_phone ? ` · ${proposal.client_phone}` : ""}</p>
            {proposal.client_address && <p className="text-sm text-white/60">Billing: {proposal.client_address}</p>}
            {proposal.project_address && <p className="text-sm text-white/60">Site: {proposal.project_address}</p>}
            <p className="text-xs text-white/40">Approved on {approvedDate}</p>
            <p className={`inline-block text-[10px] font-bold uppercase tracking-widest px-2.5 py-1.5 rounded-lg ${proposal.default_price_mode === "labor" ? "bg-sky-400/15 text-sky-300" : "bg-primary/15 text-primary"}`} data-testid="signing-supply-mode">
              {proposal.default_price_mode === "labor" ? "Labor only — materials supplied by homeowner" : "We supply labor + materials"}
            </p>
          </section>

          <section className="border border-white/10 rounded-2xl bg-card p-5" data-testid="signing-lines">
            <p className="text-[10px] font-bold uppercase tracking-widest text-primary mb-3">Your approved items</p>
            <div className="divide-y divide-white/5">
              {approvedLines.map((i, idx) => (
                <div key={i.id || idx} className="py-2.5 flex items-center justify-between gap-3" data-testid={`signing-line-${idx}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white/85 truncate">{i.name}{i.subcategory ? ` — ${i.subcategory}` : ""}</p>
                    <p className="text-[11px] text-white/45">{i.quantity} {i.unit} × {fmt(i.rate)}</p>
                  </div>
                  <p className="text-sm font-bold text-primary shrink-0">{fmt(i.total)}</p>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between border-t border-white/10 mt-2 pt-3">
              <span className="text-[10px] font-bold uppercase tracking-widest text-white/50">Approved total</span>
              <span className="font-display text-2xl text-primary" data-testid="signing-total">{fmt(approvedTotal)}</span>
            </div>
          </section>

          <section className="space-y-3" data-testid="signing-contract">
            <p className="text-[10px] font-bold uppercase tracking-widest text-primary">Your agreement</p>
            <div className="border border-white/15 rounded-2xl bg-card p-5 max-h-72 overflow-y-auto" data-testid="signing-contract-text">
              <p className="text-white/75 text-xs leading-relaxed whitespace-pre-wrap">
                {(proposal.contract_text || "")
                  .replaceAll("{client_name}", proposal.client_name)
                  .replaceAll("{project_title}", proposal.title)
                  .replaceAll("{address}", proposal.project_address || proposal.client_address || "the project address")
                  .replaceAll("{total}", fmt(approvedTotal))
                  .replaceAll("{date}", todayLong)}
              </p>
            </div>
            <input
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
              placeholder="Type your full legal name to sign"
              data-testid="signing-signature-input"
              className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-4 text-base text-white placeholder:text-white/35 focus:outline-none focus:border-primary transition-colors duration-300"
            />
            {signatureName.trim() && (
              <p className="text-xs text-white/50" data-testid="signing-signature-preview">
                Signed as: <span className="font-serif italic text-lg text-primary">{signatureName.trim()}</span> · {todayLong}
              </p>
            )}
            <button onClick={signContract} disabled={submitting} data-testid="signing-sign-button" className="w-full bg-primary text-primary-foreground py-4 font-bold uppercase tracking-widest text-sm transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60">
              {submitting ? "Signing..." : "Sign & Finish"}
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setDone({ total: approvedTotal, signed: "", pending_signature: true })} data-testid="signing-later-button" className="border border-white/25 py-3.5 text-xs font-bold uppercase tracking-widest text-white/70 hover:border-primary hover:text-primary transition-colors duration-300">
                Sign Later
              </button>
              <button onClick={() => setEditOpen(true)} data-testid="signing-edit-button" className="border border-white/25 py-3.5 text-xs font-bold uppercase tracking-widest text-white/70 hover:border-primary hover:text-primary transition-colors duration-300">
                Request a Change
              </button>
            </div>
            {editOpen && (
              <div className="space-y-3" data-testid="signing-edit-form">
                <textarea value={editNote} onChange={(e) => setEditNote(e.target.value)} rows={3} placeholder="Tell us what you'd like to change" className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-primary resize-none" data-testid="signing-edit-note" />
                <button onClick={sendEditRequest} disabled={editSending} data-testid="signing-edit-send" className="border border-primary text-primary px-6 py-3 text-xs font-bold uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors duration-300 disabled:opacity-50">
                  {editSending ? "Sending..." : "Send Request"}
                </button>
              </div>
            )}
          </section>
        </main>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-5 text-center grain relative" data-testid="proposal-approved">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
          <img src="/logo.png" alt="Infinite Siding LLC" className="h-14 w-auto logo-on-dark mx-auto" />
          <div className="w-16 h-16 bg-primary text-primary-foreground flex items-center justify-center mx-auto mt-10">
            <Check size={30} aria-hidden="true" />
          </div>
          <h1 className="font-display text-5xl md:text-7xl mt-8">PROPOSAL<br />APPROVED</h1>
          <p className="text-white/60 mt-4 max-w-md mx-auto text-sm leading-relaxed">
            Thank you, {proposal.client_name}. We've received your selection totaling <span className="text-primary font-bold">{fmt(done.total)}</span> and we'll reach out shortly to schedule the work.
          </p>
          {done.signed && (
            <p className="text-white/50 text-xs mt-5" data-testid="approved-signed">Agreement signed by <span className="font-serif italic text-base text-primary">{done.signed}</span> on {done.signed_at ? fmtDateUS(done.signed_at) : new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>
          )}
          {done.pending_signature && (
            <p className="text-amber-300/80 text-xs mt-5" data-testid="approved-sign-pending">No rush — your project is booked. Open this link anytime to sign the agreement.</p>
          )}
          <p className="text-white/40 text-xs mt-8">Questions? Call (203) 307-5222 · yali@infinitesiding.com</p>
          <a href="/projects" data-testid="approved-see-work" className="inline-block mt-6 border border-primary rounded-lg text-primary px-6 py-3 text-xs font-bold uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors duration-300">See Our Recent Work</a>
          <div className="mt-8">
            {editSent ? (
              <p className="text-primary text-sm font-semibold" data-testid="edit-request-sent">Edit request sent — we'll unlock your proposal and reach out shortly.</p>
            ) : editOpen ? (
              <div className="max-w-md mx-auto space-y-3" data-testid="request-edit-form">
                <textarea
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                  rows={3}
                  placeholder="Tell us what you'd like to change — e.g. 'I want Hardie instead of vinyl'"
                  className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-primary resize-none"
                  data-testid="request-edit-note"
                />
                <button onClick={sendEditRequest} disabled={editSending} data-testid="request-edit-send" className="border border-primary text-primary px-6 py-3 text-xs font-bold uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors duration-300 disabled:opacity-50">
                  {editSending ? "Sending..." : "Send Request"}
                </button>
              </div>
            ) : (
              <button onClick={() => setEditOpen(true)} data-testid="request-edit-button" className="border border-white/25 px-6 py-3.5 text-xs font-bold uppercase tracking-widest text-white/70 hover:border-primary hover:text-primary transition-colors duration-300">
                Request an Edit
              </button>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  if (proposal.status === "edit_requested") {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-5 text-center grain relative" data-testid="edit-requested-screen">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
          <img src="/logo.png" alt="Infinite Siding LLC" className="h-14 w-auto logo-on-dark mx-auto" />
          <h1 className="font-display text-5xl md:text-7xl mt-10">CHANGE REQUEST<br />RECEIVED</h1>
          <p className="text-white/60 mt-4 max-w-md mx-auto text-sm leading-relaxed">
            We've got your note, {proposal.client_name}. Your contractor will unlock the proposal so you can make your changes — we'll reach out shortly.
          </p>
          <p className="text-white/40 text-xs mt-8">Questions? Call (203) 307-5222</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-32" data-testid="proposal-page">
      <header className="border-b border-white/10 px-5 md:px-10 py-5 flex items-center justify-center">
        <img src="/logo.png" alt="Infinite Siding LLC" className="h-11 w-auto logo-on-dark" />
      </header>
      <main className="max-w-3xl mx-auto px-5 pt-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <p className="text-primary text-xs font-bold tracking-[0.3em] uppercase mb-3">Proposal for <span data-testid="proposal-client-name">{proposal.client_name}</span></p>
          <KineticLines lines={["BUILD YOUR", "PROJECT"]} className="font-display text-6xl md:text-8xl leading-[0.9] tracking-wide" />
          <p className="text-white/60 mt-5 text-sm md:text-base" data-testid="proposal-title">{proposal.title}</p>
          {proposal.client_address && <p className="text-white/50 text-sm mt-1.5" data-testid="proposal-address">{proposal.client_address}</p>}
          <p className={`inline-block mt-3 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1.5 rounded-lg ${proposal.default_price_mode === "labor" ? "bg-sky-400/15 text-sky-300" : "bg-primary/15 text-primary"}`} data-testid="proposal-supply-mode">
            {proposal.default_price_mode === "labor" ? "Labor only — you supply the materials" : "Labor + materials included"}
          </p>
          {proposal.notes && <p className="text-white/45 mt-4 text-sm leading-relaxed border-l-2 border-primary/50 pl-4">{proposal.notes}</p>}
          {proposal.scope_of_work && (
            <div className="mt-6 border border-white/10 rounded-xl bg-card p-4 text-left" data-testid="proposal-scope">
              <p className="text-[10px] font-bold uppercase tracking-widest text-primary mb-2">Scope of work</p>
              <p className="text-white/75 text-sm leading-relaxed whitespace-pre-line">{proposal.scope_of_work}</p>
            </div>
          )}
          <p className="text-white/40 text-xs mt-6 tracking-wide">Work through each step below — tap a material to open it, compare real photos and prices, and add what you love to your project. Your total updates live at the bottom.</p>
        </motion.div>

        {newModel ? (
          categories.map((cat) => {
            const locked = items.filter((i) => i.category === cat && (i.flag === "required" || i.flag === "included"));
            const choiceRows = items.filter((i) => i.category === cat && (i.flag === "optional" || i.flag === "alternate") && i.offered_options?.length > 0);
            if (locked.length === 0 && choiceRows.length === 0) return null;
            const mode = modes[cat] || "single";
            return (
              <section key={cat} className="mt-14" data-testid={`proposal-cat-${cat}`}>
                <h2 className="font-display text-3xl tracking-wide flex items-center gap-3 mb-2 border-b border-white/15 pb-3">
                  <span className="w-2.5 h-2.5 bg-primary rotate-45 shrink-0" />{cat}
                </h2>
                {choiceRows.length > 0 && (
                  <div className="mb-5 border-l-2 border-primary/60 pl-4 py-1" data-testid={`cat-mode-hint-${cat}`}>
                    <p className="text-sm text-white/70 leading-relaxed">
                      {mode === "multiple"
                        ? `Pick the ${cat.toLowerCase()} you'd like for your house — tap a material to see its choices and prices, then tap the one you love. You can pick from more than one.`
                        : `Pick the type of ${cat.toLowerCase()} you'd like for your house — tap a material to see its choices and prices, then tap the one you love.`}
                    </p>
                    <p className="text-[11px] text-white/35 mt-1">Anything you don't pick simply stays out of your total.</p>
                  </div>
                )}
                <div className="space-y-4">
                  {choiceRows.map((item) => (
                    <TypeCard
                      key={item.id}
                      item={item}
                      pick={(sel[cat] || []).find((p) => p.item_id === item.id)}
                      open={openTypes.has(item.id) || !!(sel[cat] || []).find((p) => p.item_id === item.id)}
                      onToggleOpen={() => toggleOpen(item.id)}
                      onPickOption={(itemId, subId) => pickOption(cat, itemId, subId)}
                      onZoom={setLightbox}
                    />
                  ))}
                  {locked.length > 0 && (
                    <div className="pt-2">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-3">Locked in — already in your price</p>
                      <div className="space-y-3">
                        {locked.map((item) => (
                          <ItemCard key={item.id} item={item} selectable={false} selected onToggle={() => {}} onZoom={setLightbox} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </section>
            );
          })
        ) : (
          categories.map((cat) => {
            const addOns = items.filter((i) => i.category === cat && i.flag === "optional");
            const groupAlts = allGroups.filter(([g, gItems]) => gItems[0].category === cat);
            if (groupAlts.length === 0 && addOns.length === 0) return null;
            return (
              <section key={cat} className="mt-12" data-testid={`proposal-cat-${cat}`}>
                <h2 className="font-display text-3xl tracking-wide flex items-center gap-3 mb-5">
                  <span className="w-2.5 h-2.5 bg-primary rotate-45 shrink-0" />{cat}
                </h2>
                <div className="space-y-4">
                  {groupAlts.map(([group, gItems]) => (
                    <ChoiceStep
                      key={group}
                      num={allGroups.findIndex(([g]) => g === group) + 1}
                      group={group}
                      gItems={gItems}
                      selectedId={altSel[group]}
                      open={openGroup === group}
                      onToggle={() => setOpenGroup(openGroup === group ? null : group)}
                      onSelect={(itemId) => {
                        setAltSel({ ...altSel, [group]: itemId });
                        setDetailId(null);
                        setTimeout(() => setOpenGroup(null), 400);
                      }}
                      detailId={detailId}
                      onToggleDetail={(iid) => setDetailId(detailId === iid ? null : iid)}
                    />
                  ))}
                  {addOns.length > 0 && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-widest text-white/40 mb-3 mt-2">Optional add-ons — tap to include</p>
                      <div className="space-y-3">
                        {addOns.map((item) => (
                          <ItemCard
                            key={item.id}
                            item={item}
                            selectable
                            selected={optionalSel.has(item.id)}
                            onToggle={() => toggleOptional(item.id)}
                            onZoom={setLightbox}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </section>
            );
          })
        )}

        {!newModel && includedItems.length > 0 && (
          <section className="mt-14" data-testid="proposal-included">
            <h2 className="font-display text-3xl tracking-wide flex items-center gap-3 mb-5">
              <span className="w-2.5 h-2.5 bg-primary rotate-45 shrink-0" />INCLUDED IN YOUR PROJECT
            </h2>
            <div className="space-y-3">
              {includedItems.map((item) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  selectable={false}
                  selected
                  onToggle={() => {}}
                  onZoom={setLightbox}
                />
              ))}
            </div>
          </section>
        )}
        {proposal.contract_text && (
          <section className="mt-14" data-testid="contract-section">
            <div className="border border-primary/30 rounded-2xl bg-primary/5 p-5 flex items-start gap-3">
              <FileSignature size={18} className="text-primary shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm text-white/70 leading-relaxed">Your agreement is ready — after you approve these prices, you'll review the contract and sign on the next screen.</p>
            </div>
          </section>
        )}
        <section className="mt-14" data-testid="client-note-section">
          <h2 className="font-display text-3xl tracking-wide flex items-center gap-3 mb-4">
            <span className="w-2.5 h-2.5 bg-primary rotate-45 shrink-0" />ANYTHING WE SHOULD KNOW?
          </h2>
          <textarea
            value={clientNote}
            onChange={(e) => setClientNote(e.target.value)}
            rows={3}
            placeholder="Colors you love, timing, questions — leave a note with your approval (optional)"
            data-testid="client-note-input"
            className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-primary resize-none transition-colors duration-300"
          />
        </section>
        <section className="mt-10" data-testid="proposal-see-work">
          <a href="/projects" data-testid="proposal-see-work-link" className="group flex items-center justify-between gap-4 border border-white/15 rounded-2xl bg-card px-5 py-5 transition-colors duration-300 hover:border-primary/50">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-primary mb-1.5">Our Recent Work</p>
              <p className="text-sm text-white/70 leading-relaxed">See the homes and buildings we've already transformed — browse our project gallery.</p>
            </div>
            <span className="flex items-center gap-2 font-display text-2xl tracking-wide text-white/80 group-hover:text-primary transition-colors duration-300 shrink-0">
              VIEW <ArrowUpRight size={20} aria-hidden="true" />
            </span>
          </a>
        </section>
      </main>

      <div className="fixed bottom-0 inset-x-0 z-40 bg-[#171914]/90 backdrop-blur-xl border-t border-white/10" data-testid="proposal-total-bar">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/50">Your total</p>
            <p className="font-display text-3xl text-primary leading-none" data-testid="proposal-total">{fmt(total)}</p>
            <p className="text-[10px] text-white/35 mt-1">Final price may vary once siding color is selected</p>
          </div>
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="proposal-approve-button"
            className="bg-primary text-primary-foreground px-6 md:px-8 py-4 font-bold uppercase tracking-widest text-xs md:text-sm transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(193,214,46,0.35)] disabled:opacity-60"
          >
            {submitting ? "Sending..." : proposal.contract_text ? "Approve & Continue to Contract" : "Approve This Proposal"}
          </button>
        </div>
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-[60] bg-[#171914]/95 flex items-center justify-center p-4 cursor-zoom-out" onClick={() => setLightbox(null)} data-testid="photo-lightbox">
          <button className="absolute top-4 right-4 text-white/70 hover:text-primary transition-colors duration-200" aria-label="Close photo" data-testid="lightbox-close">
            <X size={28} />
          </button>
          <img src={mediaUrl(lightbox)} alt="Material example — enlarged" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  );
}
